import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Loader2, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

async function getCroppedBlob(src, pixelCrop) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, pixelCrop.width, pixelCrop.height
  );
  return new Promise((res) => canvas.toBlob((b) => res(b), "image/jpeg", 0.9));
}

// aspect e.g. 16/6; imageSrc = data URL of selected file
export const ImageCropModal = ({ open, imageSrc, aspect, onCancel, onConfirm }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onComplete = useCallback((_, px) => setAreaPixels(px), []);

  const confirm = async () => {
    if (!areaPixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedBlob(imageSrc, areaPixels);
      await onConfirm(blob);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white" data-testid="image-crop-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-white">Position &amp; Crop</DialogTitle>
        </DialogHeader>
        <div className="relative w-full h-64 bg-slate-900 rounded-lg overflow-hidden">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onComplete}
            />
          )}
        </div>
        <div className="mt-4">
          <p className="text-xs text-slate-400 mb-2">Drag to reposition · use the slider to zoom</p>
          <Slider min={1} max={3} step={0.01} value={[zoom]} onValueChange={(v) => setZoom(v[0])} data-testid="crop-zoom-slider" />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onCancel} disabled={saving}
            className="rounded-full border-slate-600 text-slate-200 hover:bg-slate-700 hover:text-white" data-testid="crop-cancel">
            <X size={16} className="mr-2" /> Cancel
          </Button>
          <Button onClick={confirm} disabled={saving || !areaPixels}
            className="bg-red-600 hover:bg-red-700 font-semibold rounded-full" data-testid="crop-confirm">
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Check size={16} className="mr-2" />} Apply &amp; Upload
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
